import BathtubOutlinedIcon from '@mui/icons-material/BathtubOutlined';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import type { AdMediaType } from '../../types/ads';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';
import { priceFormat } from '../../utils';

import {
  getPropertyAdPreviewLocation,
  getPropertyAdPreviewMedia,
  getPropertyAdPreviewPrice,
  getPropertyAdPreviewPropertyKind,
  getPropertyAdPreviewSpecs,
  getPropertyAdPreviewTitle,
  type PropertyAdPreviewSpec,
} from './propertyAdPreview';

interface ListingPreviewProps {
  media: AdMediaType[];
}

const previewSpecIcon = {
  area: <SquareFootOutlinedIcon aria-hidden='true' fontSize='small' />,
  beds: <BedOutlinedIcon aria-hidden='true' fontSize='small' />,
  baths: <BathtubOutlinedIcon aria-hidden='true' fontSize='small' />,
} satisfies Record<PropertyAdPreviewSpec['kind'], JSX.Element>;

export const ListingPreview = ({ media }: ListingPreviewProps) => {
  const { control } = useFormContext<PropertyAdEditorFormValues>();
  const activityKind = useWatch({ control, name: 'activityKind' });
  const transactionType = useWatch({ control, name: 'transactionType' });
  const propertyKind = useWatch({ control, name: 'propertyKind' });
  const currency = useWatch({ control, name: 'currency' });
  const price = useWatch({ control, name: 'price' });
  const sellingPrice = useWatch({ control, name: 'sellingPrice' });
  const defaultRentFrequency = useWatch({ control, name: 'defaultRentFrequency' });
  const priceYearly = useWatch({ control, name: 'priceYearly' });
  const priceMonthly = useWatch({ control, name: 'priceMonthly' });
  const priceWeekly = useWatch({ control, name: 'priceWeekly' });
  const priceDaily = useWatch({ control, name: 'priceDaily' });
  const addressEn = useWatch({ control, name: 'addressEn' });
  const nameEn = useWatch({ control, name: 'nameEn' });
  const title = useWatch({ control, name: 'title' });
  const buildingName = useWatch({ control, name: 'buildingName' });
  const beds = useWatch({ control, name: 'beds' });
  const baths = useWatch({ control, name: 'baths' });
  const builtUpArea = useWatch({ control, name: 'builtUpArea' });
  const previewPrice = getPropertyAdPreviewPrice({
    activityKind,
    currency,
    defaultRentFrequency,
    price,
    priceDaily,
    priceMonthly,
    priceWeekly,
    priceYearly,
    sellingPrice,
    transactionType,
  });
  const locationValues = { addressEn, buildingName, nameEn, propertyKind, title };
  const previewTitle = getPropertyAdPreviewTitle(locationValues);
  const previewLocation = getPropertyAdPreviewLocation(locationValues);
  const propertyKindLabel = getPropertyAdPreviewPropertyKind(propertyKind);
  const previewSpecs = getPropertyAdPreviewSpecs({ baths, beds, builtUpArea, propertyKind });
  const { coverUrl, imageCount } = getPropertyAdPreviewMedia(media);
  const hasDetails = Boolean(
    previewTitle || previewLocation || propertyKindLabel || previewPrice || previewSpecs.length
  );

  return (
    <Paper
      aria-labelledby='listing-preview-title'
      component='section'
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #dce9f7',
        borderRadius: 2,
        boxShadow: '0 4px 16px rgba(42, 56, 66, 0.08)',
        boxSizing: 'border-box',
        minWidth: 0,
        padding: { xs: 2, sm: 2.5 },
        width: '100%',
      }}
    >
      <Typography
        color='#2a3842'
        component='h2'
        fontWeight={600}
        id='listing-preview-title'
        marginBottom={2}
        variant='h5'
      >
        Listing Preview
      </Typography>

      <Paper
        aria-label='Listing preview card'
        component='article'
        elevation={0}
        sx={{
          backgroundColor: '#f1f7ff',
          borderRadius: '14px',
          minWidth: 0,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            aspectRatio: '482 / 238',
            backgroundColor: '#e7f0fb',
            display: 'flex',
            justifyContent: 'center',
            minWidth: 0,
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
          }}
        >
          {coverUrl ? (
            <Box
              alt={previewTitle ? `${previewTitle} listing cover` : 'Listing cover'}
              component='img'
              src={coverUrl}
              sx={{ display: 'block', height: '100%', objectFit: 'cover', width: '100%' }}
            />
          ) : (
            <Stack
              alignItems='center'
              aria-label='No listing photo'
              color='#6f7d87'
              role='img'
              spacing={0.5}
            >
              <ImageOutlinedIcon aria-hidden='true' fontSize='large' />
              <Typography component='span' variant='body2'>
                No photo yet
              </Typography>
            </Stack>
          )}

          {imageCount > 0 && (
            <Stack
              alignItems='center'
              aria-label={`${imageCount} ${imageCount === 1 ? 'photo' : 'photos'}`}
              direction='row'
              spacing={0.5}
              sx={{
                backgroundColor: 'rgba(42, 56, 66, 0.8)',
                borderRadius: 1,
                bottom: 12,
                color: '#fff',
                padding: '4px 8px',
                position: 'absolute',
                right: 12,
              }}
            >
              <ImageOutlinedIcon aria-hidden='true' sx={{ fontSize: 18 }} />
              <Typography component='span' fontSize={13} fontWeight={500}>
                {imageCount}
              </Typography>
            </Stack>
          )}
        </Box>

        <Stack minWidth={0} padding={2} spacing={1.25}>
          {propertyKindLabel && (
            <Typography color='#1650ff' component='p' fontSize={13} fontWeight={600}>
              {propertyKindLabel}
            </Typography>
          )}

          {previewTitle && (
            <Typography
              color='#2a3842'
              component='h3'
              fontSize={20}
              fontWeight={600}
              lineHeight={1.3}
              sx={{ overflowWrap: 'anywhere' }}
            >
              {previewTitle}
            </Typography>
          )}

          {previewPrice && (
            <Stack alignItems='center' direction='row' flexWrap='wrap' gap={1}>
              <Typography color='#2a3842' component='span' fontSize={15} fontWeight={500}>
                Price
              </Typography>
              <Typography color='#1650ff' component='span' fontSize={17} fontWeight={600}>
                {priceFormat(previewPrice.amount)} {previewPrice.currency}
              </Typography>
              {previewPrice.period && (
                <Typography
                  color='#1650ff'
                  component='span'
                  fontSize={12}
                  fontWeight={600}
                  sx={{ border: '1px solid #1650ff', borderRadius: 4, padding: '2px 10px' }}
                >
                  {previewPrice.period}
                </Typography>
              )}
            </Stack>
          )}

          {previewLocation && (
            <Stack alignItems='flex-start' direction='row' minWidth={0} spacing={1}>
              <LocationOnOutlinedIcon
                aria-hidden='true'
                sx={{ color: '#1650ff', flex: '0 0 auto', fontSize: 20, marginTop: '2px' }}
              />
              <Typography
                color='#2a3842'
                component='p'
                fontSize={14}
                fontWeight={500}
                minWidth={0}
                sx={{ overflowWrap: 'anywhere' }}
              >
                {previewLocation}
              </Typography>
            </Stack>
          )}

          {previewSpecs.length > 0 && (
            <Stack
              alignItems='center'
              aria-label='Listing specifications'
              direction='row'
              flexWrap='wrap'
              gap={1.5}
            >
              {previewSpecs.map((specification) => (
                <Stack alignItems='center' direction='row' key={specification.kind} spacing={0.5}>
                  {previewSpecIcon[specification.kind]}
                  <Typography color='#2a3842' component='span' fontSize={14}>
                    {specification.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}

          {!hasDetails && (
            <Typography color='#6f7d87' component='p' textAlign='center' variant='body2'>
              Listing details will appear here.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Paper>
  );
};
